import { useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ChevronRight, Clock, ShieldCheck, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchRuns, useAsync } from "@/lib/queries";
import { formatDate, formatTime, progressOf, todayISO } from "@/lib/constants";
import { StatusBadge, ProgressBar, StabVestBadge } from "@/components/StatusBadge";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { CompanyBar } from "@/components/CompanyBar";

const TABS = [
  { id: "today", label: "Today", test: (d, t) => d === t },
  { id: "upcoming", label: "Upcoming", test: (d, t) => d > t },
  { id: "past", label: "Past", test: (d, t) => d < t },
];

const JobCard = ({ run }) => {
  const p = progressOf(run.run_addresses);
  return (
    <Link to={`/jobs/${run.id}`} data-testid={`job-card-${run.id}`} className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md active:bg-slate-50">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-data text-xs uppercase tracking-wider text-slate-500">{formatDate(run.run_date)}</p>
          <p className="font-display mt-1 flex items-center gap-2 text-lg font-bold text-slate-900"><Clock className="h-4 w-4 text-blue-700" />Start {formatTime(run.start_time)}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusBadge status={run.status} />
          {run.stab_vest_required && <StabVestBadge testId={`job-card-stab-vest-${run.id}`} />}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
        <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />{run.officer?.full_name || "Officer"}</span>
        <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{p.total} address{p.total === 1 ? "" : "es"}</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1"><ProgressBar done={p.done} total={p.total} /></div>
        <ChevronRight className="h-5 w-5 text-slate-400" />
      </div>
    </Link>
  );
};

export default function MyJobs() {
  const { profile } = useAuth();
  const [tab, setTab] = useState("today");
  const ready = !!profile.company?.active;
  const { data, loading, error, reload } = useAsync(() => (ready ? fetchRuns({ locksmithId: profile.id }) : Promise.resolve(null)), [profile.id, profile.company_id, ready]);
  const today = todayISO();
  const hasToday = (data || []).some((r) => r.run_date === today);
  const tabs = hasToday ? TABS : TABS.filter((t) => t.id !== "today");
  const activeTab = !hasToday && tab === "today" ? "upcoming" : tab;
  const current = TABS.find((t) => t.id === activeTab);
  const runs = (data || []).filter((r) => current.test(r.run_date, today));
  if (activeTab !== "past") runs.sort((a, b) => a.run_date.localeCompare(b.run_date) || a.start_time.localeCompare(b.start_time));

  return (
    <div className="space-y-5" data-testid="my-jobs-page">
      <PageHeader eyebrow={`Hi ${profile.full_name?.split(" ")[0] || "there"}`} title="My Jobs" />
      <CompanyBar onChanged={reload} />
      {data && (
      <div className={`grid ${tabs.length === 3 ? "grid-cols-3" : "grid-cols-2"} gap-1 rounded-xl bg-slate-200/70 p-1`} role="tablist">
        {tabs.map((t) => (
          <button key={t.id} role="tab" aria-selected={activeTab === t.id} onClick={() => setTab(t.id)} data-testid={`my-jobs-tab-${t.id}`}
            className={`min-h-[44px] rounded-lg text-sm font-semibold transition-colors ${activeTab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            {t.label}
          </button>
        ))}
      </div>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && ready && !data && <LoadingRows rows={3} />}
      {data && !runs.length && <EmptyState icon={Briefcase} title={`No ${current.label.toLowerCase()} runs`} description="Runs assigned to you by a manager will appear here." testId="my-jobs-empty" />}
      <div className="space-y-3">{runs.map((r) => <JobCard key={r.id} run={r} />)}</div>
    </div>
  );
}
